import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { evaluateExerciseSubmission } from '@org/utils-client';
import type { ExerciseEvaluationResult } from '@org/utils-client';
import { isP2EErrorKey } from '@org/i18n';
import { useTranslation } from 'react-i18next';
import type { StudentExerciseContent } from './use-class-blocks';
import type { ExerciseSubmission } from './content-renderer-types';
import type {
  ExerciseFeedbackMap,
  ExerciseFeedbackMessage,
} from './content-renderer-types';
import type { LatestSubmissionResult, SubmitExerciseResult } from '../types/exercise';

// ─────────────────────────────────────────────────────────────────────────────
// Pure utility functions (exported for reuse in components)
// ─────────────────────────────────────────────────────────────────────────────

interface AutoEvaluationSummary {
  title: string;
  description?: string;
  isSuccess: boolean;
}

/**
 * Determines if an auto-evaluation result should be considered successful.
 * Checks `isFullyCorrect` first, then falls back to score comparison.
 */
export function resolveAutoEvaluationSuccess(
  result: Partial<Pick<ExerciseEvaluationResult, 'isFullyCorrect' | 'score' | 'maxScore'>>,
): boolean {
  if (typeof result.isFullyCorrect === 'boolean') {
    return result.isFullyCorrect;
  }

  if (typeof result.score === 'number' && typeof result.maxScore === 'number') {
    if (result.maxScore <= 0) {
      return result.score > 0;
    }
    return result.score >= result.maxScore;
  }

  return false;
}

/**
 * Generates a human-readable summary from an auto-evaluation result.
 * Returns exercise-type-specific messages based on the evaluation details.
 */
export function summarizeAutoEvaluation(result: ExerciseEvaluationResult): AutoEvaluationSummary {
  const isSuccessful = resolveAutoEvaluationSuccess(result);

  switch (result.details.kind) {
    case 'MultipleChoice': {
      // Submissions stored before Jan 2026 have no `optionEvaluations` (only the
      // selected/correct indices), so treat a missing list as empty.
      const optionEvaluations = result.details.optionEvaluations ?? [];

      // If every option is correct, any selection should be accepted as valid
      const allOptionsCorrect =
        optionEvaluations.length > 0 && optionEvaluations.every((option) => option.isCorrectAnswer);

      // Fallback: detect all-options-correct by comparing the stored correct
      // indices with the option list. Without the list there is nothing to compare
      // against, so those rows rely on the stored `isFullyCorrect`/score instead.
      const allCorrectViaIndices =
        optionEvaluations.length > 0 &&
        (result.details.correctOptionIndices?.length ?? 0) === optionEvaluations.length;

      return isSuccessful || allOptionsCorrect || allCorrectViaIndices
        ? { title: 'Correct answer!', description: 'Great job - keep it up!', isSuccess: true }
        : {
            title: 'Not quite there yet',
            description: 'Take another look at the options and try again.',
            isSuccess: false,
          };
    }
    case 'FillInBlank': {
      const sentences = Array.isArray(result.details.sentences) ? result.details.sentences : [];
      const totals = sentences.reduce(
        (acc, sentence) => {
          const blanks =
            Array.isArray((sentence as any).blanks) && (sentence as any).blanks.length
              ? (sentence as any).blanks
              : [
                  {
                    isCorrect: (sentence as any).isCorrect ?? false,
                    grade: (sentence as any).grade,
                  },
                ];
          const totalBlanks = blanks.length;
          const correctBlanks = blanks.filter((blank: any) => blank.isCorrect).length;
          return {
            total: acc.total + totalBlanks,
            correct: acc.correct + correctBlanks,
          };
        },
        { total: 0, correct: 0 },
      );
      return isSuccessful
        ? { title: 'Perfect!', description: 'All blanks are filled correctly.', isSuccess: true }
        : {
            title: 'Keep practicing',
            description: `${totals.correct} of ${totals.total} blanks are correct so far.`,
            isSuccess: false,
          };
    }
    case 'SelectForBlank': {
      const sentences = Array.isArray(result.details.sentences) ? result.details.sentences : [];
      const totals = sentences.reduce(
        (acc, sentence) => {
          const blanks = Array.isArray(sentence.blanks) ? sentence.blanks : [];
          const totalBlanks = blanks.length;
          const correctBlanks = blanks.filter((blank) => blank.isCorrect).length;
          return {
            total: acc.total + totalBlanks,
            correct: acc.correct + correctBlanks,
          };
        },
        { total: 0, correct: 0 },
      );

      return isSuccessful
        ? { title: 'Excellent!', description: 'Every selection is spot on.', isSuccess: true }
        : {
            title: 'Keep practicing',
            description: `${totals.correct} of ${totals.total} selections are correct so far.`,
            isSuccess: false,
          };
    }
    default:
      return isSuccessful
        ? { title: 'Great job!', isSuccess: true }
        : { title: 'Keep trying...', isSuccess: false };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook types
// ─────────────────────────────────────────────────────────────────────────────

interface UseExerciseManagerOptions {
  submitExercise: (variables: {
    exerciseId: string;
    submission: ExerciseSubmission;
  }) => Promise<SubmitExerciseResult>;
  exercisesUtils: any;
}

interface UseExerciseManagerReturn {
  exerciseFeedback: ExerciseFeedbackMap;
  handleSubmit: (content: StudentExerciseContent, submission: ExerciseSubmission) => Promise<void>;
  seedInitialFeedback: (submissions: Record<string, LatestSubmissionResult>) => void;
  clearFeedback: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

function buildFeedbackFromSubmission(
  submission: LatestSubmissionResult | undefined,
): ExerciseFeedbackMessage[] {
  if (!submission?.evaluation) {
    return [];
  }

  const messages: ExerciseFeedbackMessage[] = [];
  const evaluation = submission.evaluation as Record<string, unknown>;
  const auto = evaluation?.auto as Partial<ExerciseEvaluationResult> | undefined;

  if (auto && auto.details) {
    const score = typeof auto.score === 'number' ? auto.score : 0;
    const maxScore = typeof auto.maxScore === 'number' ? auto.maxScore : 0;
    const isFullyCorrect = resolveAutoEvaluationSuccess({
      isFullyCorrect: typeof auto.isFullyCorrect === 'boolean' ? auto.isFullyCorrect : undefined,
      score,
      maxScore,
    });

    const autoResult: ExerciseEvaluationResult = {
      score,
      maxScore,
      isFullyCorrect,
      details: auto.details as ExerciseEvaluationResult['details'],
    };
    const summary = summarizeAutoEvaluation(autoResult);
    messages.push({
      source: 'auto',
      title: summary.title,
      description: summary.description,
      variant: summary.isSuccess ? 'success' : 'warning',
    });
  }

  const ai = evaluation?.ai as
    | { feedback?: string | null; annotatedSubmission?: string | null }
    | undefined;
  const annotatedText = typeof ai?.annotatedSubmission === 'string' ? ai.annotatedSubmission : null;

  if (ai?.feedback || annotatedText) {
    messages.push({
      source: 'ai',
      title: 'Submission graded',
      description: ai?.feedback ?? undefined,
      variant: 'info',
      annotatedText,
    });
  }

  // Add teacher comment feedback
  const teacher = evaluation?.teacher as
    | { comment?: string | null; updatedAt?: string; teacherId?: string }
    | undefined;
  if (teacher?.comment) {
    messages.push({
      source: 'teacher',
      title: 'Personal comment',
      description: teacher.comment,
      variant: 'teacher',
    });
  }

  return messages;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Consolidated hook for managing exercise submissions and feedback.
 *
 * Combines the functionality of:
 * - useExerciseFeedback (feedback state management)
 * - useExerciseSubmission (submission handling)
 * - useAutoEvaluationSummary (evaluation summarization)
 */
export function useExerciseManager({
  submitExercise,
  exercisesUtils,
}: UseExerciseManagerOptions): UseExerciseManagerReturn {
  const { t } = useTranslation('admin');
  const [exerciseFeedback, setExerciseFeedback] = useState<ExerciseFeedbackMap>({});

  const seedInitialFeedback = useCallback(
    (submissions: Record<string, LatestSubmissionResult>) => {
      setExerciseFeedback((previous) => {
        let changed = false;
        const next = { ...previous };

        Object.entries(submissions).forEach(([exerciseId, submission]) => {
          if (!previous[exerciseId]) {
            const messages = buildFeedbackFromSubmission(submission);
            if (messages.length) {
              next[exerciseId] = messages;
              changed = true;
            }
          }
        });

        return changed ? next : previous;
      });
    },
    [],
  );

  const clearFeedback = useCallback(() => {
    setExerciseFeedback({});
  }, []);

  const handleSubmit = useCallback(
    async (content: StudentExerciseContent, submission: ExerciseSubmission) => {
      if (!content.exerciseId) {
        toast.error('This exercise is missing required metadata. Please contact your instructor.');
        return;
      }

      const definition = content.evaluationDefinition ?? null;
      const messages: ExerciseFeedbackMessage[] = [];

      // Run client-side auto-evaluation if definition is available
      if (definition) {
        const autoEvaluation = evaluateExerciseSubmission(definition, submission);
        if (autoEvaluation) {
          const summary = summarizeAutoEvaluation(autoEvaluation);
          messages.push({
            source: 'auto',
            title: summary.title,
            description: summary.description,
            variant: summary.isSuccess ? 'success' : 'warning',
          });
        }
      }

      try {
        const result = await submitExercise({
          exerciseId: content.exerciseId,
          submission,
        });

        const aiFeedback = result?.evaluation?.ai?.feedback ?? null;
        const aiAnnotated =
          typeof result?.evaluation?.ai?.annotatedSubmission === 'string'
            ? result.evaluation?.ai?.annotatedSubmission
            : null;

        if (aiFeedback || aiAnnotated) {
          messages.push({
            source: 'ai',
            title: 'Submission graded',
            description: aiFeedback ?? undefined,
            variant: 'info',
            annotatedText: aiAnnotated ?? undefined,
          });
        } else if (messages.length === 0) {
          messages.push({
            source: 'status',
            title: 'Answer submitted!',
            variant: 'success',
          });
        }

        // Invalidate cache first
        if (exercisesUtils?.getLatestSubmissions?.invalidate) {
          await exercisesUtils.getLatestSubmissions.invalidate();
        }

        // Update feedback after cache invalidation so field colors and feedback sync
        setExerciseFeedback((previous) => ({
          ...previous,
          [content.exerciseId]: messages,
        }));
      } catch (error) {
        const message =
          (error as any)?.data?.json?.message ??
          (error as any)?.shape?.message ??
          (error as any)?.message;
        const normalized = typeof message === 'string' ? message.trim() : '';
        const translated = normalized && isP2EErrorKey(normalized) ? t(normalized) : null;
        // Anything that is not one of our own translated error codes is a technical
        // failure (e.g. the AI grader choking) — log it, never show it raw to students.
        if (!translated) {
          console.error('Exercise submission failed', error);
        }
        toast.error(
          translated && translated.trim().length > 0
            ? translated
            : t('p2e.errors.submissionFailed'),
          { duration: 8000 },
        );
      }
    },
    [exercisesUtils, submitExercise, t],
  );

  return {
    exerciseFeedback,
    handleSubmit,
    seedInitialFeedback,
    clearFeedback,
  };
}
